import { useEffect, useRef, useState } from "react";
import { ClipboardPaste, X } from "lucide-react";
import type { ScreenField, ScreenNoun } from "@sloe/schema";
import { notifySafeCheckpoint, useReloadGuard } from "../../lib/update-reload";
import { interactiveAccent } from "../../lib/brand";

type Props = {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  workspaceId: string;
  screenId: string;
  screenLabel: string;
  noun: ScreenNoun;
  fields: ScreenField[];
  brandColor?: string;
};

function sampleText(fields: ScreenField[], noun: ScreenNoun): string {
  const usable = fields.filter((field) => !["id", "created_at", "updated_at"].includes(field.key)).slice(0, 4);
  const headers = usable.length ? usable.map((field) => field.label || field.key) : ["Name", "Stage", "Owner"];
  const first = headers.map((header, index) => {
    if (/stage|status|phase/i.test(header)) return "Active";
    if (/owner|assigned/i.test(header)) return "Team";
    if (/value|amount|price/i.test(header)) return "1000";
    return `Example ${noun.singular} ${index + 1}`;
  });
  return `${headers.join(",")}\n${first.join(",")}`;
}

export function BulkImportModal({
  open,
  onClose,
  onImported,
  workspaceId,
  screenId,
  screenLabel,
  noun,
  fields,
  brandColor,
}: Props) {
  const [text, setText] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const previousOpenRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setText("");
    setSourceName("");
    setSubmitting(false);
    setError(null);
    setResult(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, submitting, onClose]);

  const hasDraft = text.trim().length > 0 || sourceName.trim().length > 0;

  useReloadGuard({
    id: `bulk-import:${workspaceId}:${screenId}`,
    active: open,
    busy: open && submitting,
    dirty: open,
    label: `Import ${noun.plural}`,
  });

  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;
    if (wasOpen && !open) {
      const closedSafely = Boolean(result) || (!submitting && !hasDraft);
      if (closedSafely) notifySafeCheckpoint("bulk-import-close");
    }
  }, [open, result, submitting, hasDraft]);

  const submit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/screen-data/bulk", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: workspaceId,
          screen_id: screenId,
          pasted_text: text,
          source_name: sourceName.trim() || `${screenLabel} import`,
          source_type: "paste",
          limit: 500,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setResult(json);
      onImported();
    } catch (err: any) {
      setError(err?.message || "Couldn't import records");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const example = sampleText(fields, noun);
  const importedCount = Number(result?.synced_count ?? result?.inserted_count ?? 0);

  return (
    <>
      <div onClick={() => !submitting && onClose()} className="fixed inset-0 z-50 bg-black/40" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-zinc-200 p-6">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Bring in existing {noun.plural}</h2>
              <p className="mt-1 text-sm text-zinc-500">Paste rows from a spreadsheet. The Agentic Operating System maps columns to this screen.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-50"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto p-6">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Source name</span>
              <input
                value={sourceName}
                onChange={(event) => setSourceName(event.target.value)}
                placeholder={`${screenLabel} import`}
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Rows</span>
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder={example}
                className="mt-1 min-h-56 w-full resize-y rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm leading-6 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
            </label>
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600">
              First row should be headers. CSV, tab-separated rows, and JSON row arrays are supported. Up to 500 rows import in one run.
            </div>
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}
            {result && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                Imported {importedCount} {importedCount === 1 ? noun.singular : noun.plural}. This screen has been refreshed.
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-zinc-200 p-6">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-xl border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              {result ? "Done" : "Cancel"}
            </button>
            {!result && (
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !text.trim()}
                style={brandColor ? { backgroundColor: interactiveAccent(brandColor) } : undefined}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                  brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
                }`}
              >
                <ClipboardPaste className="h-4 w-4" />
                {submitting ? "Importing..." : "Import rows"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
